import { useState } from 'react'
import {Sparkles, Check, ChevronDown, ChevronUp, Leaf, FileText, GraduationCap, Building2} from 'lucide-react'
import SobreMonica from '../components/SobreMonica'
import PlanosAssinatura from '../components/PlanosAssinatura'
import { ASSINATURA } from '../utils/assinatura'
import { OLEOS_PROTOCOLO } from '../utils/protocolosEssenciais'
import { CURSOS_AROMATERAPIA_LISTA } from '../data/cursosAromaterapia'

// 5 cursos de aromaterapia + o introdutório "Uma Gota de Equilíbrio".
const TOTAL_CURSOS = CURSOS_AROMATERAPIA_LISTA.length + 1

// Total real do banco de protocolos, arredondado para baixo na dezena.
const OLEOS_ARREDONDADO =
  Math.floor(OLEOS_PROTOCOLO.length / 10) * 10

const FEATURES = [
  {
    title: 'Diagnóstico adulto com link personalizado',
    desc: 'Compartilhe seu link exclusivo com clientes, amigos e grupos no WhatsApp. Cada pessoa preenche o diagnóstico gratuitamente e o resultado aparece no seu painel automaticamente.',
  },
  {
    title: 'Diagnóstico kids com link personalizado',
    desc: 'Link exclusivo para o diagnóstico infantil. Compartilhe com mães e responsáveis e receba o resultado organizado no seu painel.',
  },
  {
    title: 'Painel de contatos completo',
    desc: 'Todos os seus leads organizados em um único lugar com histórico, status de cada atendimento e óleos indicados para cada pessoa.',
  },
  {
    title: 'Script de vendas automático',
    desc: 'Três mensagens geradas automaticamente para cada cliente baseadas no perfil e no momento dele. Copie e envie direto pelo WhatsApp.',
  },
  {
    title: 'Óleos essenciais indicados automaticamente',
    desc: 'O sistema identifica os óleos certos para cada pessoa com base nas respostas do diagnóstico incluindo regras de segurança para gestantes, hipertensos e epiléticos.',
  },
  {
    title: 'Banco de protocolos de óleos essenciais',
    desc: `Mais de ${OLEOS_ARREDONDADO} óleos essenciais com ficha pronta de uso: situações de uso, modo aromático, tópico e interno, e a diluição indicada. Busque o óleo, selecione quantos quiser e copie o protocolo para enviar ao cliente pelo WhatsApp.`,
  },
  {
    title: 'Opções de compra calculadas automaticamente',
    desc: 'Três opções geradas automaticamente com valores, PV, frete e parcelamento para Brasil e Portugal.',
  },
  {
    title: 'Protocolo de uso personalizado',
    desc: 'Instruções completas de como usar cada óleo indicado com rotina diária de manhã, tarde e noite.',
  },
  {
    title: 'Calculadora de óleos e venda avulsa',
    desc: 'Calcule o valor de qualquer combinação de óleos essenciais na hora do atendimento com total, frete e parcelamento automáticos. Venda óleos avulsos com praticidade e agilidade.',
  },
  {
    title: 'Criativos e panfletos prontos para divulgar',
    desc: 'Materiais de divulgação prontos para usar no Instagram, WhatsApp e onde quiser. Criativos e panfletos personalizados com seu link do diagnóstico.',
  },
  {
    title: 'Como divulgar seu diagnóstico',
    desc: 'Abordagens prontas para divulgar seu link para amigos, grupos no WhatsApp e redes sociais. Copie e use imediatamente.',
  },
  {
    title: 'Como usar o sistema',
    desc: 'Aulas exclusivas ensinando como começar e como dar o retorno para o cliente após o diagnóstico.',
  },
  {
    title: 'Curso Uma Gota de Equilíbrio com certificado',
    desc: '7 aulas introdutórias sobre óleos essenciais com quiz por aula e certificado de conclusão em Aromaterapia Introdutória gerado automaticamente.',
  },
  {
    title: 'Jornada das 12 Emoções, sua ou para presentear',
    desc: 'Toda assinante ganha 1 acesso gratuito à Jornada Completa. Depois, você pode vender essa experiência para suas clientes ou comprar créditos promocionais para presentear quem você quiser.',
  },
  {
    title: '5 cursos de aromaterapia para estudar e compartilhar',
    desc: 'Aromaterapia Prática e Consciente, para Crianças, para Dores, Energética e Vibracional. Acesso completo, com liberdade para distribuir gratuitamente e sem limites para clientes e equipe.',
  },
  {
    title: 'Suporte a Brasil e Portugal',
    desc: 'Preços em Real e Euro, formatos de WhatsApp corretos para cada país e kits específicos de cada mercado.',
  },
]

const VIDEOS = [
  {
    title: 'Como é o sistema por dentro e como divulgar o seu link do diagnóstico',
    desc: 'Conheça o painel completo, todas as funcionalidades e aprenda a compartilhar seu link para captar clientes qualificados.',
    embedUrl: 'https://www.youtube.com/embed/a5NUZJmd6es',
  },
  {
    title: 'Como funciona o diagnóstico por dentro e o que cada pergunta vai lhe indicar',
    desc: 'Entenda como o diagnóstico funciona, o que cada pergunta avalia e como o sistema indica os óleos essenciais certos para cada pessoa.',
    embedUrl: 'https://www.youtube.com/embed/R9EAYdNhDFg',
  },
  {
    title: 'Como visualizar as respostas do diagnóstico do cliente e atendê-lo',
    desc: 'Veja como acessar os resultados do diagnóstico no seu painel e como usar o script automático para dar o retorno certo para cada cliente.',
    embedUrl: 'https://www.youtube.com/embed/0VngQUV--fI',
  },
]

function irParaPlanos() {
  document.getElementById('planos')?.scrollIntoView({ behavior: 'smooth' })
}

const FAQS = [
  {
    q: 'Preciso ter experiência com tecnologia?',
    a: 'Não. O Sistema Descubra seus Óleos foi desenvolvido para ser simples e intuitivo. Se você sabe usar o WhatsApp, sabe usar o Sistema Descubra seus Óleos.',
  },
  {
    q: 'Funciona para Portugal também?',
    a: 'Sim. O Sistema Descubra seus Óleos tem suporte completo para Portugal com preços em Euro, kits específicos e formato de WhatsApp correto.',
  },
  {
    q: 'Posso cancelar quando quiser?',
    a: 'A assinatura é anual. Você tem 7 dias após a compra para solicitar o reembolso integral, conforme o direito de arrependimento. Depois disso, o acesso segue ativo durante todo o período contratado.',
  },
  {
    q: 'O diagnóstico é gratuito para os meus clientes?',
    a: 'Sim. Seus clientes preenchem o diagnóstico gratuitamente. Você paga apenas pela sua assinatura como consultor(a).',
  },
  {
    q: 'Quantos clientes posso atender?',
    a: 'Ilimitado. Não há limite de leads ou atendimentos.',
  },
  {
    q: 'A Jornada das 12 Emoções é gratuita?',
    a: 'Sim! Toda assinante recebe 1 acesso gratuito para viver a jornada completa. Depois disso, você pode comprar créditos para presentear suas clientes ou revender a experiência.',
  },
  {
    q: 'Os cursos de aromaterapia têm custo adicional?',
    a: 'Não. Todos os 5 cursos temáticos, além do curso introdutório, estão inclusos na sua assinatura, sem custo extra e sem limite de compartilhamento.',
  },
]

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-[#EDE0D4] rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 sm:px-6 py-4 min-h-[48px] text-left bg-white hover:bg-[#F8F4EF] transition-colors"
      >
        <span className="font-medium text-[#2C2C2A] text-sm md:text-base pr-4">{q}</span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-[#2D6A4F] shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#2D6A4F] shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 sm:px-6 py-4 bg-[#F8F4EF] border-t border-[#EDE0D4]">
          <p className="text-[#5F5E5A] text-sm leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  )
}

function Renovar() {
  return (
    <div className="min-h-screen bg-[#F8F4EF]">
      {/* Header nav */}
      <header className="sticky top-0 z-40 bg-[#F8F4EF]/90 backdrop-blur-sm border-b border-[#EDE0D4]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <a
            href="/assinar"
            className="flex items-center gap-2 min-w-0"
          >
            <div className="shrink-0 w-8 h-8 sm:w-9 sm:h-9 bg-[#2D6A4F] rounded-full flex items-center justify-center">
              <Sparkles className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-white" />
            </div>
            <span className="font-cormorant font-semibold text-[#2C2C2A] text-sm sm:text-lg md:text-xl leading-tight truncate">
              Sistema Descubra seus Óleos
            </span>
          </a>
          <nav className="flex items-center gap-2 sm:gap-3 shrink-0">
            <a
              href="https://www.descubraseusoleos.com.br/admin"
              className="inline-flex items-center justify-center min-h-[40px] sm:min-h-[44px] px-3 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-medium text-[#2D6A4F] border border-[#2D6A4F] hover:bg-[#D8F3DC] transition-colors"
            >
              Entrar
            </a>
            <button
              type="button"
              onClick={irParaPlanos}
              className="inline-flex items-center justify-center min-h-[40px] sm:min-h-[44px] px-3 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-semibold text-white bg-[#2D6A4F] hover:bg-opacity-90 transition-colors"
            >
              Assinar
            </button>
          </nav>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-10 space-y-10 sm:space-y-14">

        {/* ── HERO ── */}
        <section className="text-center space-y-5">
          <p className="text-[#B08968] text-xs sm:text-sm tracking-[0.08em] uppercase font-medium">
            Um sistema completo para consultoras de óleos essenciais
          </p>
          <h1 className="font-cormorant font-semibold text-[#2C2C2A] text-3xl sm:text-4xl md:text-5xl leading-tight">
            Atenda seus clientes com poucos cliques.
          </h1>
          <div className="max-w-2xl mx-auto space-y-3 sm:space-y-4">
            <p className="text-[#5F5E5A] text-base sm:text-lg leading-relaxed">
              Você não precisa saber tudo para atender bem. Comece hoje, sem estudar e sem experiência com óleos essenciais.
            </p>
            <p className="text-[#5F5E5A] text-base sm:text-lg leading-relaxed">
              Envie aos seus clientes um link de diagnóstico que avalia a saúde física e emocional, e o sistema indica os óleos essenciais mais adequados e os protocolos de uso. Você também recebe o passo a passo para divulgar e o material pronto.
            </p>
            <p className="text-[#5F5E5A] text-base sm:text-lg leading-relaxed">
              Atenda com alto padrão e surpreenda seus clientes, com rapidez e facilidade.
            </p>
          </div>
          <a
            href={ASSINATURA.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center min-h-[48px] px-8 py-3 rounded-full text-sm sm:text-base font-semibold text-white bg-[#2D6A4F] hover:bg-opacity-90 transition-colors"
          >
            {ASSINATURA.botao}
          </a>

          <p className="text-[#5F5E5A] text-xs sm:text-sm [font-variant-numeric:lining-nums_tabular-nums]">
            ou {ASSINATURA.parcelado}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 sm:gap-4 pt-4 border-t border-[#EDE0D4]">
            {[
              {
                icone: Leaf,
                texto: `Mais de ${OLEOS_ARREDONDADO} protocolos de óleos essenciais`,
              },
              {
                icone: FileText,
                texto: 'Diagnóstico adulto e infantil em link, para enviar aos seus clientes',
              },
              {
                icone: GraduationCap,
                texto: `${TOTAL_CURSOS} cursos de aromaterapia para estudar e compartilhar`,
              },
              {
                icone: Building2,
                texto: 'Área Empresas com contratos e relatórios',
              },
            ].map(({ icone: Icone, texto }) => (
              <div
                key={texto}
                className="flex sm:flex-col items-center sm:items-center gap-2 sm:gap-1.5 justify-start sm:justify-start text-left sm:text-center"
              >
                <Icone className="w-4 h-4 text-[#2D6A4F] shrink-0" strokeWidth={1.8} />
                <span className="text-[#5F5E5A] text-xs leading-snug">{texto}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── PARA QUEM É ── */}
        <section className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 md:p-10 border border-[#EDE0D4]">
          <h2 className="font-cormorant font-semibold text-[#2D6A4F] text-2xl sm:text-3xl mb-5">Para quem é</h2>
          <div className="space-y-4 text-[#5F5E5A] leading-relaxed">
            <p>
              Esse é o <strong className="text-[#2C2C2A]">Sistema Descubra seus Óleos</strong>, criado para <strong className="text-[#2C2C2A]">consultores(as) de óleos essenciais e terapeutas</strong> que querem atender com mais profissionalismo, organizar os contatos e conduzir cada atendimento com método, sem precisar improvisar.
            </p>
            <p className="bg-[#FFF7F0] border-l-4 border-[#B08968] rounded-r-xl px-5 py-4 text-sm">
              Se você já perdeu uma venda por não saber o que falar, por não ter as informações do cliente organizadas ou por não saber por onde começar, <strong className="text-[#2C2C2A]">o Sistema Descubra seus Óleos foi feito para você.</strong>
            </p>
            <p>
              O <strong className="text-[#2C2C2A]">Sistema Descubra seus Óleos</strong> também é para <strong className="text-[#2C2C2A]">terapeutas, psicólogos, nutricionistas, coaches, educadores físicos e profissionais de saúde e bem-estar</strong> que desejam ampliar sua atuação integrando os óleos essenciais às suas práticas. O sistema se torna uma ferramenta de captação de clientes e de diferenciação no seu atendimento, sem que você precise abandonar o que já faz.
            </p>
          </div>
        </section>

        {/* ── O PROBLEMA QUE RESOLVE ── */}
        <section className="bg-[#2D6A4F] rounded-2xl p-6 sm:p-8 md:p-10 text-white">
          <h2 className="font-cormorant font-semibold text-2xl sm:text-3xl mb-6">O problema que resolve</h2>
          <p className="text-white/80 mb-5 text-sm">Sem o Sistema Descubra seus Óleos o(a) consultor(a):</p>
          <ul className="space-y-3 mb-8">
            {[
              'Não sabe quais óleos indicar para cada pessoa',
              'Improvisa cada atendimento do zero',
              'Não tem um script para conduzir a conversa com segurança',
              'Perde leads por falta de acompanhamento',
              'Não consegue escalar os atendimentos',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm leading-relaxed">
                <span className="mt-0.5 text-[#52B788] font-bold text-base shrink-0">→</span>
                <span className="text-white/90">{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-[#D8F3DC] font-medium text-base">
            Com o Diagnóstico de Saúde e Emoções tudo isso muda.
          </p>
        </section>

        {/* ── O QUE ESTÁ INCLUÍDO ── */}
        <section>
          <h2 className="font-cormorant font-semibold text-[#2C2C2A] text-2xl sm:text-3xl mb-3 text-center">O que está incluído</h2>
          <p className="text-center text-[#5F5E5A] text-sm sm:text-base mb-7 max-w-xl mx-auto leading-relaxed">
            O Sistema Descubra seus Óleos completo para consultores(as) de óleos essenciais crescerem com profissionalismo e estratégia.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-5 sm:p-6 border border-[#EDE0D4] shadow-sm flex gap-4">
                <div className="shrink-0 w-7 h-7 bg-[#D8F3DC] rounded-full flex items-center justify-center mt-0.5">
                  <Check className="w-4 h-4 text-[#2D6A4F]" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="font-medium text-[#2C2C2A] text-sm mb-1">{f.title}</p>
                  <p className="text-[#5F5E5A] text-xs leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
            <div className="bg-[#D8F3DC]/40 rounded-2xl p-5 sm:p-6 border-2 border-[#2D6A4F] shadow-sm flex gap-4 md:col-span-2">
              <div className="shrink-0 w-7 h-7 bg-[#2D6A4F] rounded-full flex items-center justify-center mt-0.5">
                <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="font-medium text-[#2C2C2A] text-sm">Área Empresas com contratos e relatórios</p>
                </div>
                <p className="text-[#5F5E5A] text-xs leading-relaxed">
                  A área Empresas inclui diagnóstico corporativo e NR-1 para você entrar em empresas, gerar relatórios da equipe e abrir portas para novos serviços.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── ASSINATURA ── */}
        <section id="planos">
          <div className="bg-white p-6 sm:p-8 md:p-10 rounded-2xl shadow-lg border border-[#EDE0D4]">
            <div className="text-center mb-8">
              <h3 className="text-2xl sm:text-3xl font-cormorant font-semibold text-[#2C2C2A] mb-2 leading-tight">
                O que você recebe
              </h3>
              <p className="text-[#5F5E5A] text-sm">Todos os recursos do sistema em um único acesso</p>
            </div>

            <PlanosAssinatura />

            <p className="text-xs text-[#5F5E5A] text-center mt-6 mb-3">
              Pagamento seguro processado pela Hotmart.
            </p>
            <p className="text-center">
              <a
                href="https://wa.me/5581988671210"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#2D6A4F] underline hover:opacity-80 transition-opacity"
              >
                Dúvidas? Fale com a Mônica
              </a>
            </p>
          </div>
        </section>

        {/* ── VÍDEOS ── */}
        <section>
          <h2 className="font-cormorant font-semibold text-[#2C2C2A] text-2xl sm:text-3xl mb-3 text-center">
            Veja como funciona na prática
          </h2>
          <p className="text-center text-[#5F5E5A] text-sm sm:text-base mb-7 max-w-xl mx-auto leading-relaxed">
            Em poucos minutos você entende como usar o sistema e já pode começar a captar clientes.
          </p>
          <div className="space-y-5">
            {VIDEOS.map((video) => (
              <article
                key={video.title}
                className="bg-white rounded-2xl border border-[#EDE0D4] shadow-sm overflow-hidden"
              >
                <div className="relative bg-[#2C2C2A] aspect-video overflow-hidden">
                  <iframe
                    src={video.embedUrl}
                    title={video.title}
                    className="absolute inset-0 w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>
                <div className="p-5 sm:p-6">
                  <h3 className="font-medium text-[#2C2C2A] text-sm sm:text-base mb-2 leading-snug">
                    {video.title}
                  </h3>
                  <p className="text-[#5F5E5A] text-sm leading-relaxed">{video.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <div className="flex justify-center">
          <a
            href={ASSINATURA.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center min-h-[48px] px-8 py-3 rounded-full text-sm sm:text-base font-semibold text-white bg-[#2D6A4F] hover:bg-opacity-90 transition-colors"
          >
            {ASSINATURA.botao}
          </a>
        </div>

        {/* ── FAQ ── */}
        <section>
          <h2 className="font-cormorant font-semibold text-[#2C2C2A] text-2xl sm:text-3xl mb-7 text-center">Perguntas frequentes</h2>
          <div className="space-y-3">
            {FAQS.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </section>

        <p className="text-center">
          <a
            href="https://wa.me/5581988671210"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[#2D6A4F] underline hover:opacity-80 transition-opacity"
          >
            Dúvidas? Fale com a Mônica
          </a>
        </p>

        {/* ── SOBRE A CRIADORA ── */}
        <SobreMonica variante="consultora" mostrarInstagram />

        <div className="pb-10" />
      </div>
    </div>
  )
}

export default Renovar
